// src/pages/auth/ForgotPasswordPage.jsx
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/marketing/Navbar';
import Turnstile from '../../components/common/Turnstile';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  const handleChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };

  const validateEmail = () => {
    if (!email.trim()) {
      setError('E-mailadres is verplicht');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Voer een geldig e-mailadres in');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateEmail()) {
      return;
    }

    if (!turnstileToken) {
      setError('Bevestig dat je geen robot bent');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, turnstileToken })
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        setTurnstileToken('');
      } else {
        setError(data.message || 'Er is een fout opgetreden. Probeer het opnieuw.');
        setTurnstileToken('');
        turnstileRef.current?.reset();
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setError('Er is een fout opgetreden. Controleer je internetverbinding en probeer het opnieuw.');
      setTurnstileToken('');
      turnstileRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
        <Navbar />
        
        <div className="pt-24 pb-12 px-4">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8 mt-8 text-center">
            {/* Success Icon */}
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="text-3xl font-light text-purple-800 mb-4">
              E-mail Verzonden!
            </h1>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-purple-900 mb-2">
                Als er een account bestaat met dit e-mailadres, hebben we een herstellink gestuurd naar:
              </p>
              <p className="font-semibold text-purple-800">
                {email}
              </p>
            </div>

            <div className="text-left bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="font-medium text-gray-800 mb-2">Volgende stappen:</p>
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                <li>Check je inbox (en spam map)</li>
                <li>Klik op de herstellink in de e-mail</li>
                <li>Stel een nieuw wachtwoord in</li>
                <li>Log in met je nieuwe wachtwoord</li>
              </ol>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>Let op:</strong> De link is 1 uur geldig
              </p>
            </div>

            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full px-6 py-3 bg-purple-500 text-white font-semibold rounded-md hover:bg-purple-600 transition-colors text-center"
              >
                Terug naar Inloggen
              </Link>
              <button
                onClick={() => setIsSuccess(false)}
                className="block w-full px-6 py-2 border border-purple-500 text-purple-600 font-semibold rounded-md hover:bg-purple-50 transition-colors"
              >
                Verstuur Opnieuw
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-100 font-['Lexend_Deca']">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8 mt-8">
          <h1 className="text-3xl font-light text-purple-800 mb-2 text-center">
            Wachtwoord Vergeten?
          </h1>
          <p className="text-center text-gray-600 mb-6">
            Geen probleem! Voer je e-mailadres in en we sturen je een herstellink.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                E-mailadres
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                  error ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="jan@voorbeeld.nl"
                autoFocus
              />
            </div>

            <Turnstile
              ref={turnstileRef}
              onVerify={setTurnstileToken}
              onExpire={() => setTurnstileToken('')}
            />

            <button
              type="submit"
              disabled={isSubmitting || !turnstileToken}
              className={`w-full py-3 px-4 bg-purple-500 text-white font-semibold rounded-md transition-colors ${
                isSubmitting || !turnstileToken
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-purple-600'
              }`}
            >
              {isSubmitting ? 'Bezig met verzenden...' : 'Herstellink Versturen'}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <Link to="/login" className="block text-purple-600 hover:text-purple-800 font-medium text-sm">
              ← Terug naar inloggen
            </Link>
            <div className="text-sm text-gray-600">
              Geen account?{' '}
              <Link to="/register" className="text-purple-600 hover:text-purple-800 font-medium">
                Registreer hier
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;